import * as React from 'react';
import { cn } from '@lib/utils';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { FC } from 'react';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-xs', className)}
      {...props}
    />
  </div>
));

Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('[&_tr]:border-b text-sm', className)}
    {...props}
  />
));

TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));

TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
));

TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className
    )}
    {...props}
  />
));

TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-2 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
));

TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'p-2 py-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
));

TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));

TableCaption.displayName = 'TableCaption';

export type TColumn<T> = {
  title: string | React.ReactNode;
  className?: string;
  dataIndex?: keyof T;
  render: (item?: T, index?: number, v?: any) => React.ReactNode;
};

export type TTableProps<T> = {
  columns: TColumn<T>[];
  data: T[];
  onSelect?: (column: T) => void;
  variant?: 'surface' | 'ghost';
};

const RadixTable: FC<TTableProps<any>> = ({
  data,
  columns,
  onSelect,
  variant = 'ghost',
}) => {
  const tableClass =
    variant === 'surface'
      ? 'bg-transparent border border-gray-300 rounded-[3px]'
      : 'bg-transparent border-none';

  return (
    <ScrollArea.Root className={`w-full overflow-hidden ${tableClass}`}>
      <ScrollArea.Viewport className="overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 border-b">
            <tr>
              {columns?.map((column, i) => (
                <th
                  key={i}
                  className={cn(
                    'text-left py-3 px-1 text-sm font-bold',
                    column.className
                  )}
                >
                  {typeof column.title === 'string' ? (
                    <p>{column.title}</p>
                  ) : (
                    column.title
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((item, index) => {
              return (
                <tr
                  key={index}
                  className={cn(
                    'border-t hover:bg-accent text-xs',
                    onSelect ? 'cursor-pointer' : ''
                  )}
                >
                  {columns?.map((column, i) => {
                    return (
                      <td
                        key={i}
                        className={cn('relative px-1 py-4', column.className)}
                        onClick={() => onSelect && onSelect(item)}
                      >
                        {column.render(
                          item,
                          index,
                          item?.[column.dataIndex || '']
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        className="h-2 bg-[#5f5f5f3d] rounded"
        orientation="horizontal"
      >
        <ScrollArea.Thumb className="bg-[#5f5f5f] rounded" />
      </ScrollArea.Scrollbar>
      <ScrollArea.Scrollbar
        className="w-2 bg-[#5f5f5f3d] rounded"
        orientation="vertical"
      >
        <ScrollArea.Thumb className="bg-[#5f5f5f] rounded" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
};

export {
  RadixTable,
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
